import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import DTFMenu from "@/components/DTFMenu";
import SCMenu from "@/components/SCMenu";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/quote", label: "Quote" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  React.useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      className="sticky top-0 z-50 bg-[color:var(--bg)] border-b-2 border-black"
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-3 items-center px-4 md:px-8 h-24 md:h-32">
        {/* Left: mobile menu toggle */}
        <div className="flex items-center lg:hidden">
          <button
            className="p-2 border border-black"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Left (desktop): main nav */}
        <nav className="hidden lg:flex items-center gap-6 justify-self-start">
          <NavLink
            to="/"
            end
            data-testid="nav-link-home"
            className={({ isActive }) =>
              `label-mono underline-mono inline-flex items-center gap-1.5 ${isActive ? "text-[color:var(--primary)]" : "text-black"}`
            }
          >
            Home
          </NavLink>
          <DTFMenu variant="nav" data-testid="nav-dtf-menu" />
          <SCMenu variant="nav" data-testid="nav-sc-menu" />
        </nav>

        {/* Center: two brand logos with an ampersand riding the divider line */}
        <div className="flex items-center justify-center gap-2 md:gap-4" data-testid="brand-logo">
          <Link
            to="/shop/dtf"
            className="flex flex-col items-center gap-2 md:gap-3 group"
            data-testid="nav-brand-dtf-link"
          >
            <img
              src="/logos/down-to-film.png"
              alt="Down To Film Apparel"
              className="h-14 md:h-20 lg:h-24 w-auto object-contain transition-transform group-hover:-translate-y-0.5"
              data-testid="nav-logo-dtf"
            />
            <span className="label-mono font-bold text-[13px] md:text-[16px] text-dtf-blue tracking-[0.2em]">
              APPAREL
            </span>
          </Link>
          <div
            className="relative flex items-center self-stretch px-2 md:px-3"
            aria-hidden
            data-testid="nav-brand-ampersand-wrap"
          >
            {/* Divider */}
            <span className="absolute inset-y-4 md:inset-y-3 left-1/2 -translate-x-1/2 w-px bg-black/30" />
            {/* Ampersand sitting right on top of the divider line */}
            <span
              className="relative font-display font-black leading-none text-3xl md:text-5xl lg:text-6xl px-1 md:px-1.5 bg-[color:var(--bg)]"
              style={{
                color: "#000",
                textShadow: "1px 1px 0 var(--dtf-yellow), 2px 2px 0 var(--sc-pink)",
              }}
              data-testid="nav-brand-ampersand"
            >
              &amp;
            </span>
          </div>
          <Link
            to="/shop/tsc"
            className="flex flex-col items-center gap-2 md:gap-3 group"
            data-testid="nav-brand-sc-link"
          >
            <img
              src="/logos/sticky-chick.webp"
              alt="The Sticky Chick — Stickers"
              className="h-14 md:h-20 lg:h-24 w-auto object-contain transition-transform group-hover:-translate-y-0.5 scale-[1.35]"
              data-testid="nav-logo-sc"
            />
            <span className="label-mono font-bold text-[13px] md:text-[16px] text-sc-purple tracking-[0.2em]">
              STICKERS
            </span>
          </Link>
        </div>

        {/* Right: shop/about/quote/phone/CTA */}
        <div className="hidden lg:flex items-center gap-5 justify-self-end">
          <NavLink
            to="/shop"
            data-testid="nav-link-shop"
            className={({ isActive }) =>
              `label-mono underline-mono ${isActive ? "text-[color:var(--primary)]" : "text-black"}`
            }
          >
            Shop
          </NavLink>
          <NavLink
            to="/about"
            data-testid="nav-link-about"
            className={({ isActive }) =>
              `label-mono underline-mono ${isActive ? "text-[color:var(--primary)]" : "text-black"}`
            }
          >
            About
          </NavLink>
          <NavLink
            to="/quote"
            data-testid="nav-link-quote"
            className={({ isActive }) =>
              `label-mono underline-mono ${isActive ? "text-[color:var(--primary)]" : "text-black"}`
            }
          >
            Quote
          </NavLink>
          <a
            href="tel:+19099313557"
            className="label-mono inline-flex items-center gap-2 hover:text-[color:var(--primary)]"
            data-testid="nav-phone"
          >
            <Phone size={14} /> (909) 931-3557
          </a>
          <Link
            to="/quote"
            className="btn-brutal btn-brutal-red inline-flex items-center justify-center text-center whitespace-nowrap min-w-[128px] h-11 px-5 leading-tight"
            data-testid="nav-get-quote-btn"
          >
            Get A Quote
          </Link>
        </div>

        {/* Right (mobile): spacer for grid symmetry */}
        <div className="lg:hidden" />
      </div>

      {open && (
        <div className="lg:hidden border-t border-black bg-[color:var(--bg)]">
          <div className="flex flex-col p-4 gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="label-mono py-2 border-b border-black/20"
              >
                {l.label}
              </NavLink>
            ))}
            <div className="border border-black p-3">
              <p className="label-mono text-dtf-blue mb-2">Down To Film — Standard Line</p>
              <div className="flex flex-col gap-1">
                <NavLink to="/shop?category=tees" data-testid="mobile-dtf-tees" className="label-mono py-1">→ T-Shirts</NavLink>
                <NavLink to="/shop?category=tanks" data-testid="mobile-dtf-tanks" className="label-mono py-1">→ Tank Tops</NavLink>
                <NavLink to="/shop?category=hoodies" data-testid="mobile-dtf-hoodies" className="label-mono py-1">→ Hoodies</NavLink>
                <NavLink to="/shop?category=zip-ups" data-testid="mobile-dtf-zipups" className="label-mono py-1">→ Zip-Ups</NavLink>
                <NavLink to="/shop?category=gangsheets" data-testid="mobile-dtf-gangsheets" className="label-mono py-1">→ Gang Sheets</NavLink>
                <NavLink to="/quote" data-testid="mobile-dtf-other" className="label-mono py-1 mt-1 pt-2 border-t border-black/20">→ Anything else? Request a quote</NavLink>
              </div>
            </div>
            <div className="border border-black p-3">
              <p className="label-mono text-sc-purple mb-1">The Sticky Chick — Standard Line</p>
              <p className="label-mono text-[color:var(--muted-text)] text-[10px] mb-2">2-Day Production</p>
              <div className="flex flex-col gap-1">
                <NavLink to="/shop?category=stickers" data-testid="mobile-sc-stickers" className="label-mono py-1">→ Stickers (Peel & Stick)</NavLink>
                <NavLink to="/shop?category=transfer-decals" data-testid="mobile-sc-decals" className="label-mono py-1">→ Decals (Transfer Tape)</NavLink>
                <NavLink to="/shop?category=card-stickers" data-testid="mobile-sc-card-stickers" className="label-mono py-1">→ Credit/Debit Card Stickers</NavLink>
                <NavLink to="/shop?category=label-sheets" data-testid="mobile-sc-label-sheets" className="label-mono py-1">→ Label Sheets</NavLink>
                <NavLink to="/shop?category=door-signs" data-testid="mobile-sc-door-signs" className="label-mono py-1">→ Door Signs</NavLink>
                <NavLink to="/shop?category=signage" data-testid="mobile-sc-signage" className="label-mono py-1">→ Signage</NavLink>
                <NavLink to="/shop?category=marketing" data-testid="mobile-sc-marketing" className="label-mono py-1 mt-1 pt-2 border-t border-black/20">→ Misc Marketing Material</NavLink>
                <NavLink to="/quote" data-testid="mobile-sc-other" className="label-mono py-1">→ Anything else? Request a quote</NavLink>
              </div>
            </div>
            <Link
              to="/quote"
              className="btn-brutal btn-brutal-red mt-2"
              data-testid="mobile-nav-get-quote"
            >
              Get A Quote
            </Link>
            <a
              href="tel:+19099313557"
              className="label-mono inline-flex items-center gap-2 py-2 mt-1"
              data-testid="mobile-nav-phone"
            >
              <Phone size={14} /> (909) 931-3557
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
